
import { useCallback, useState, useEffect } from 'react';
import { useCarousel } from "@/components/ui/carousel";
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ProductImageCarouselNavigationProps {
  onCarouselNavigation?: () => void;
  totalImageCount?: number;
}

export const ProductImageCarouselNavigation = ({ onCarouselNavigation, totalImageCount }: ProductImageCarouselNavigationProps) => {
  const { api, scrollPrev, scrollNext } = useCarousel();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    onSelect();

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  const handlePrevClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Navigate to previous slide without clearing color selection
    if (api) {
      scrollPrev();
    }
  }, [api, scrollPrev]);

  const handleNextClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Navigate to next slide without clearing color selection
    if (api) {
      scrollNext();
    }
  }, [api, scrollNext]);

  // Show navigation if there are multiple images
  const showNavigation = (totalImageCount || 0) > 1;

  if (!showNavigation) return null;

  return (
    <>
      <button
        onClick={handlePrevClick}
        className="absolute left-2 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full border border-white/20 bg-black/50 backdrop-blur-sm shadow-lg hover:bg-black/70 text-white transition-all duration-200 flex items-center justify-center cursor-pointer"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      
      <button
        onClick={handleNextClick}
        className="absolute right-2 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full border border-white/20 bg-black/50 backdrop-blur-sm shadow-lg hover:bg-black/70 text-white transition-all duration-200 flex items-center justify-center cursor-pointer"
        aria-label="Next slide"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Navigation dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex gap-2">
        {Array.from({ length: totalImageCount || 0 }).map((_, index) => (
          <button
            key={index}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              api?.scrollTo(index);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-200 ${
              current === index 
                ? 'bg-white shadow-md' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </>
  );
};
